import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { deleteDataAPI, getDataAPI, postDataAPI, putDataAPI } from '../../Helpers/FetchAPI';
import useUser from '../../Hooks/useUser.js';
import { logger } from '../../utils/logger.js';

export const NotebooksContext = createContext({
  notebooks: [],
  journals: [],
  isLoading: false,
  error: null,
  addNewEntry: () => {},
  updateEntry: () => {},
  deleteEntry: () => {},
  addNewJournal: () => {}
});

const NotebooksContextProvider = ({ children }) => {
  const [notebooks, setNotebooks] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [callback, setCallback] = useState(true);

  const { currentUser: userData } = useUser();

  const getNotebooks = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!userData) return;

      const res = await getDataAPI(`/api/user/notebooks/${userData._id}`);

      setNotebooks(res.data.data);
      setCallback(false);
    } catch (err) {
      logger.error('Error getting notebooks for user', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [userData]);

  const addNewEntry = useCallback(async ({ workbookId, userId, entry, title, creditId }, entryCallback) => {
    const payload = {
      workbookId,
      userId,
      entry,
      title,
      creditId
    };
    try {
      const data = await postDataAPI('/api/notebook', payload);
      setCallback(true);
      if (entryCallback) {
        entryCallback(data);
      }
    } catch (err) {
      logger.error('Error adding new entry: ', payload, err);
      setError(err);
    }
  }, []);

  const addNewJournal = useCallback(async ({ userId, entry, title }, entryCallback) => {
    const payload = {
      userId,
      entry,
      title
    };
    try {
      await postDataAPI('/api/journal', payload);
      setCallback(true);
      if (entryCallback) {
        entryCallback();
      }
    } catch (err) {
      logger.error('Error adding new journal', payload, err);
      setError(err);
    }
  }, []);

  const updateEntry = useCallback(async ({ _id, notebookId, content, title }, entryCallback) => {
    try {
      await putDataAPI(`/api/notebook/${_id}/entry`, {
        entry: content,
        title: title
      });

      const result = await getDataAPI(`/api/notebook/${notebookId}`).then((res) => {
        return res.data.data;
      });

      setNotebooks((prev) => [
        ...prev.map((notebook) => {
          if (notebook._id === result._id) {
            notebook.entries = result.entries;
            notebook.updatedAt = result.updatedAt;
          }

          return notebook;
        })
      ]);

      if (entryCallback) {
        entryCallback();
      }
    } catch (err) {
      logger.error('Error updating notebook entry for notebook id: %s', notebookId, err);
      setError(err);
    }
  }, []);

  const deleteEntry = useCallback(async ({ _id, notebookId }, entryCallback) => {
    try {
      await deleteDataAPI(`/api/notebook/${_id}/entry`);

      const result = await getDataAPI(`/api/notebook/${notebookId}`).then((res) => {
        return res.data.data;
      });

      setNotebooks((prev) => [
        ...prev.map((notebook) => {
          if (notebook._id === result._id) {
            notebook.entries = result.entries;
            notebook.updatedAt = result.updatedAt;
          }

          return notebook;
        })
      ]);

      if (entryCallback) {
        entryCallback();
      }
    } catch (err) {
      logger.error('Error deleting notebook entry', _id, notebookId, err);
      setError(err);
    }
  }, []);

  const journals = useMemo(() => notebooks.filter((item) => item.type === 'journal'), [notebooks]);

  useEffect(() => {
    let isActive = true;

    if (isActive) {
      if (userData || callback) {
        getNotebooks();
      }
    }

    return () => {
      isActive = false;
    };
  }, [getNotebooks, userData, callback]);

  const value = useMemo(() => {
    return {
      notebooks: notebooks.filter((item) => item.type === 'notes'),
      error,
      isLoading,
      addNewEntry,
      updateEntry,
      deleteEntry,
      addNewJournal,
      journals
    };
  }, [notebooks, isLoading, error, addNewEntry, updateEntry, deleteEntry, addNewJournal, journals]);
  return <NotebooksContext.Provider value={value}>{children}</NotebooksContext.Provider>;
};

export default NotebooksContextProvider;
